import {
  Box,
  List,
  ListItem,
  Popover,
  PopoverAnchor,
  PopoverContent,
  SimpleGrid,
  chakra,
} from "@chakra-ui/react";
import { ReactElement, RefObject, useEffect, useMemo, useState } from "react";
import { Article } from "../../DataSource";
import { ELLIPSISE } from "../../core/utils";
import Cell from "./Cell";
import { AxisOptions, PopoverPath } from "./types";

interface CountsGridProps {
  xAxis: string;
  yAxis: string;
  gridRef: RefObject<HTMLDivElement>;
  gridRect: DOMRect | null;
  counts: Record<string, Record<string, number>>;
  axisOptions: AxisOptions;
  popoverPath: PopoverPath | null;
  onClose: () => void;
  onPopoverPathChange: (xOption: string, yOption: string) => void;
  onArticleChange: (article: Article) => void;
}

const MAX_CELL_SIZE_PX = 60;

export function CountsGrid(props: CountsGridProps): ReactElement {
  const {
    xAxis,
    yAxis,
    gridRef,
    gridRect,
    counts,
    axisOptions,
    popoverPath,
    onClose,
    onPopoverPathChange,
    onArticleChange,
  } = props;

  const countsAggregated = useMemo<{ min: number; max: number }>(
    () =>
      Object.values(counts).reduce(
        (acc: { min: number; max: number }, counts) =>
          Object.values(counts).reduce(
            ({ min, max }, n) => ({
              min: Math.min(min, n),
              max: Math.max(max, n),
            }),
            acc
          ),
        { min: Infinity, max: -Infinity }
      ),
    [counts]
  );

  const [cellSize, setCellSize] = useState<number>(0);

  const aspectRatio = useMemo(
    () => axisOptions.x.length / axisOptions.y.length,
    [axisOptions.x.length, axisOptions.y.length]
  );

  useEffect(() => {
    if (gridRect) {
      setCellSize(
        Math.min(
          (gridRect.width - 2) / axisOptions.x.length - 1,
          MAX_CELL_SIZE_PX
        )
      );
    }
  }, [gridRect, axisOptions]);

  const maxDimValue = useMemo(
    () =>
      `min(20em, ${
        Math.max(axisOptions.x.length, axisOptions.y.length) * MAX_CELL_SIZE_PX
      }px)`,
    [axisOptions.x.length, axisOptions.y.length]
  );

  return (
    <Popover
      isOpen={popoverPath != null}
      onClose={onClose}
      placement="top-start"
      offset={[
        (popoverPath?.x.index ?? 0) * cellSize,
        -(popoverPath?.y.index ?? 0) * cellSize,
      ]}
    >
      <PopoverAnchor>
        <Box
          ref={gridRef}
          h={
            aspectRatio > 1
              ? `calc(${maxDimValue} / ${aspectRatio})`
              : maxDimValue
          }
          w={
            aspectRatio > 1
              ? maxDimValue
              : `calc(${maxDimValue} * ${aspectRatio})`
          }
        >
          <SimpleGrid
            columns={axisOptions.x.length}
            w="100%"
            h="100%"
            gap="1px"
            bg="gray.600"
            borderWidth="1px"
            borderColor="gray.600"
            alignItems="stretch"
            justifyItems="stretch"
          >
            {axisOptions.y.map((yValue) =>
              axisOptions.x.map((xValue) => (
                <Cell
                  key={`${xValue}, ${yValue}`}
                  n={counts[xValue][yValue] ?? 0}
                  minN={countsAggregated.min}
                  maxN={countsAggregated.max}
                  w={`${cellSize}px`}
                  h={`${cellSize}px`}
                  _hover={{
                    cursor:
                      counts[xValue][yValue] != null ? "pointer" : "default",
                  }}
                  onClick={
                    counts[xValue][yValue] != null
                      ? () => onPopoverPathChange(xValue, yValue)
                      : undefined
                  }
                  data-x-axis={xAxis}
                  data-x-option={xValue}
                  data-y-axis={yAxis}
                  data-y-option={yValue}
                />
              ))
            )}
          </SimpleGrid>
        </Box>
      </PopoverAnchor>
      <PopoverContent
        maxH="56"
        overflowY="auto"
        zIndex="popover"
        data-map-highlight-click-ignore
      >
        <List
          bg="white"
          display="flex"
          flexDirection="column"
          role="menu"
          fontSize="md"
          rounded="md"
        >
          {popoverPath?.articles.map((article, i) => (
            <ListItem
              key={article.id}
              w="100%"
              px="4"
              py="2"
              roundedTop={i === 0 ? "md" : undefined}
              roundedBottom={
                i === popoverPath.articles.length - 1 ? "md" : undefined
              }
              cursor="pointer"
              role="menuitemcheckbox"
              _hover={{ bg: "gray.100" }}
              {...ELLIPSISE}
              onClick={(evt) => {
                evt.stopPropagation();
                onArticleChange(article);
                onClose();
              }}
            >
              {(article.shortDescription?.length ?? 0) > 0 ? (
                article.shortDescription
              ) : (
                <chakra.em>Description not found</chakra.em>
              )}
            </ListItem>
          ))}
        </List>
      </PopoverContent>
    </Popover>
  );
}
